import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/admin", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const res = await api.post(
          "/auth/session",
          {},
          { headers: { "X-Session-Id": sessionId } }
        );
        setUser(res.data);
        // Clear hash and go to admin dashboard
        window.history.replaceState(null, "", window.location.pathname);
        if (res.data.is_admin) {
          navigate("/admin/dashboard", { replace: true, state: { user: res.data } });
        } else {
          navigate("/admin?unauthorized=1", { replace: true });
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Unknown error";
        const status = e?.response?.status || "?";
        // Show error on this screen so we can debug, AND keep a fallback redirect
        setErrMsg(`[${status}] ${detail}`);
        // Also log to console for debugging
        // eslint-disable-next-line no-console
        console.error("Auth session exchange failed:", { status, detail, error: e });
      }
    })();
  }, [navigate, setUser]);

  if (errMsg) {
    return (
      <div className="admin-shell flex items-center justify-center min-h-screen p-6">
        <div className="max-w-lg w-full bg-[#1f1f1f] border-2 border-atc-red p-8">
          <div className="overline mb-3 text-atc-red">Sign-in failed</div>
          <h1 className="font-display text-3xl uppercase text-white leading-none mb-4">Something went wrong.</h1>
          <p className="text-white/70 text-sm mb-2">The Google sign-in came back, but the server couldn't finish setting up your session.</p>
          <pre className="bg-atc-black text-atc-red font-mono text-xs p-3 border border-[#333] overflow-auto whitespace-pre-wrap break-words" data-testid="auth-error-detail">{errMsg}</pre>
          <button
            onClick={() => { window.location.href = "/admin"; }}
            className="btn-primary mt-6"
            data-testid="try-again-btn"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="font-display text-2xl uppercase">Signing you in…</div>
        <div className="font-mono text-xs text-white/50 mt-2 tracking-widest uppercase">ATC · Secure session</div>
      </div>
    </div>
  );
}
